import List from "@mui/material/List";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Dispatch,
  FC,
  SetStateAction,
  memo,
  useCallback,
  useEffect,
  useState,
} from "react";
import FoodTagSwitch from "components/food/ui/TagSwitch";
import {
  FoodItemTag,
  FoodTag,
  FoodTagCategory,
} from "client/jspPlatformExperiment";
import styled from "styled-components";
import Typography from "@mui/material/Typography";
import { useAppSelector } from "hooks/useReduxStore";
import foodItemOptionsSelector from "store/selectors/food/foodItemOptionsSelector";
import { getFoodItemOptionName } from "utils/food/foodItemOptionDisplayUtils";
import foodTagOptionsSelector from "store/selectors/food/foodTagOptionsSelector";
import {
  byDietOrder,
  byLiquidThicknessOrder,
  byTagName,
  byTextureOrder,
  PREFERENCE_SUBCATEGORIES,
  preferenceSubcategoryIndex,
} from "utils/food/foodTagDisplayUtils";

const StyledTagCategoryHeader = styled(Typography)`
  font-size: 1rem;
  font-weight: 200;
  padding: 8px 0 2px 0;
  color: #ccc;
`;

interface FoodItemTagEditSectionProps {
  foodItemTags: FoodItemTag[];
  setFoodItemTags: Dispatch<SetStateAction<FoodTag[]>>;
  aggregatingFromFoodIds?: number[] | undefined;
  viewOnlyAllergens?: boolean;
  isDish?: boolean;
  /**
   * True when editing a variant (not the main dish). A food item tag flagged
   * is_variant_identity defines the variant's identity and is edited via the
   * Variant Type dialog, so its toggle is shown read-only on the variant. All
   * other tags stay editable — even ones that also belong to the variant-type
   * category — and on the main every toggle stays editable.
   */
  isVariant?: boolean;
}

const FoodItemTagEditSection: FC<FoodItemTagEditSectionProps> = ({
  foodItemTags,
  setFoodItemTags,
  aggregatingFromFoodIds,
  viewOnlyAllergens = false,
  isDish = false,
  isVariant = false,
}) => {
  const foodTagOptions = useAppSelector(foodTagOptionsSelector.foodTagOptions);
  const idToFoodItemOption = useAppSelector(
    foodItemOptionsSelector.idToFoodItemOptionRecords,
  );
  const [allergenSources, setAllergenSources] = useState<Map<number, string[]>>(
    new Map([]),
  );
  /** The preference sections are long, so they start collapsed */
  const [isPreferencesShown, setIsPreferencesShown] = useState(false);

  /**
   * Summary the allergy source food item names
   */
  useEffect(() => {
    const newAllergenSources = new Map<number, string[]>([]);
    aggregatingFromFoodIds?.forEach((foodId) => {
      const sourceFoodItem = idToFoodItemOption[foodId];
      if (sourceFoodItem) {
        const sourceFoodItemOptionName = getFoodItemOptionName(sourceFoodItem);
        sourceFoodItem?.food_tags
          ?.filter((foodTag) =>
            foodTag.tag_categories.includes(FoodTagCategory.ALLERGEN),
          )
          .forEach((foodTag) => {
            const sourceIngNameList = newAllergenSources.get(foodTag.id);
            if (sourceIngNameList) {
              if (!sourceIngNameList.includes(sourceFoodItemOptionName)) {
                sourceIngNameList.push(sourceFoodItemOptionName);
              }
            } else {
              newAllergenSources.set(foodTag.id, [sourceFoodItemOptionName]);
            }
          });
      }
    });
    setAllergenSources(newAllergenSources);
  }, [idToFoodItemOption, aggregatingFromFoodIds]);

  /**
   * Add/remove tag from food item tags
   */
  const handleTagChange = useCallback(
    (changeTag: FoodTag) => {
      const changeTagIdx: number = foodItemTags.findIndex(
        (foodItemTag) => foodItemTag.id === changeTag.id,
      );
      if (changeTagIdx !== -1) {
        setFoodItemTags(
          foodItemTags.filter((foodItemTag) => foodItemTag.id !== changeTag.id),
        );
      } else {
        setFoodItemTags([...foodItemTags.slice(), changeTag as FoodItemTag]);
      }
    },
    [foodItemTags, setFoodItemTags],
  );

  /**
   * Get the default state by checking if the tag is in the food tags
   */
  const getDefaultState = useCallback(
    (checkTag: FoodTag) => {
      return (
        foodItemTags.find((foodItemTag) => foodItemTag.id === checkTag.id) !==
        undefined
      );
    },
    [foodItemTags],
  );

  /**
   * On a variant, the food item tag(s) flagged is_variant_identity define the
   * variant's identity and are edited only via the Variant Type dialog, so
   * their switches are locked here. Any other tag — including one that
   * belongs to the variant-type category without being this variant's
   * identity — stays editable.
   */
  const isLockedVariantIdentityTag = useCallback(
    (checkTag: FoodTag) =>
      isVariant &&
      !!foodItemTags.find((foodItemTag) => foodItemTag.id === checkTag.id)
        ?.is_variant_identity,
    [isVariant, foodItemTags],
  );

  /**
   * Get the allergy source food item names
   */
  const getAllergenSourceFoodNames = useCallback(
    (tag: FoodTag) => {
      // Find data source from client side
      let description = "";
      const allergenSourcesIng = allergenSources.get(tag.id);
      if (allergenSourcesIng) {
        allergenSourcesIng.forEach((foodOptionName) => {
          if (description !== "") {
            description = `${description} | ${foodOptionName}`;
          } else {
            description = `${foodOptionName}`;
          }
        });
      }
      // Find data source from server side
      const serverSideDataSource = foodItemTags.find(
        (foodItemTag) => foodItemTag.id === tag.id,
      )?.data_source;
      // Manage to return
      if (serverSideDataSource) {
        return `Source: ${serverSideDataSource}`;
      }
      return description;
    },
    [allergenSources, foodItemTags],
  );

  return (
    <>
      <List>
        <StyledTagCategoryHeader>
          Allergies {viewOnlyAllergens ? `(view only)` : null}
        </StyledTagCategoryHeader>
        {foodTagOptions
          .filter(
            (foodTagOption) =>
              foodTagOption.tag_categories.includes(FoodTagCategory.ALLERGEN) &&
              !foodTagOption.tag_categories.includes(
                FoodTagCategory.VARIANT_TYPE,
              ) &&
              (!viewOnlyAllergens || getDefaultState(foodTagOption)),
          )
          .sort(byTagName)
          .map((foodTagOption) => {
            return (
              <FoodTagSwitch
                key={`foodTag${foodTagOption.id}`}
                foodTag={foodTagOption}
                onChange={handleTagChange}
                defaultState={getDefaultState(foodTagOption)}
                hints={getAllergenSourceFoodNames(foodTagOption)}
                viewOnly={viewOnlyAllergens}
              />
            );
          })}
      </List>
      <List>
        <StyledTagCategoryHeader>
          Contain {viewOnlyAllergens ? `(view only)` : null}
        </StyledTagCategoryHeader>
        {foodTagOptions
          .filter(
            (foodTagOption) =>
              foodTagOption.tag_categories.includes(
                FoodTagCategory.FOOD_CATEGORY,
              ) &&
              !foodTagOption.tag_categories.includes(
                FoodTagCategory.ALLERGEN,
              ) &&
              !foodTagOption.tag_categories.includes(
                FoodTagCategory.VARIANT_TYPE,
              ) &&
              (!viewOnlyAllergens || getDefaultState(foodTagOption)),
          )
          .sort(byTagName)
          .map((foodTagOption) => {
            return (
              <FoodTagSwitch
                key={`foodTag${foodTagOption.id}`}
                foodTag={foodTagOption}
                onChange={handleTagChange}
                defaultState={getDefaultState(foodTagOption)}
                hints={getAllergenSourceFoodNames(foodTagOption)}
                viewOnly={viewOnlyAllergens}
              />
            );
          })}
      </List>
      {isDish ? (
        <>
          <List>
            <StyledTagCategoryHeader>
              Suitable Diet Types
            </StyledTagCategoryHeader>
            {foodTagOptions
              .filter(
                (foodTagOption) =>
                  foodTagOption.tag_categories.includes(
                    FoodTagCategory.DIET_TYPE,
                  ) &&
                  !foodTagOption.tag_categories.includes(
                    FoodTagCategory.ALLERGEN,
                  ),
              )
              .sort(byDietOrder)
              .map((foodTagOption) => {
                // A diet tag flagged as this variant's identity is edited via
                // the Variant Type dialog, so it is read-only here (and
                // flagged in a warning colour). Every other diet tag stays
                // editable, even one in the variant-type category.
                const isVariantIdentityTag =
                  isLockedVariantIdentityTag(foodTagOption);
                return (
                  <FoodTagSwitch
                    key={`foodTag${foodTagOption.id}`}
                    foodTag={foodTagOption}
                    onChange={handleTagChange}
                    defaultState={getDefaultState(foodTagOption)}
                    hints={getAllergenSourceFoodNames(foodTagOption)}
                    viewOnly={isVariantIdentityTag}
                    warn={isVariantIdentityTag}
                  />
                );
              })}
          </List>
          <List>
            <StyledTagCategoryHeader>
              Suitable Food Textures
            </StyledTagCategoryHeader>
            {foodTagOptions
              .filter(
                (foodTagOption) =>
                  foodTagOption.tag_categories.includes(
                    FoodTagCategory.FOOD_TEXTURE,
                  ) &&
                  !(
                    foodTagOption.tag_categories.includes(
                      FoodTagCategory.ALLERGEN,
                    ) ||
                    foodTagOption.tag_categories.includes(
                      FoodTagCategory.DIET_TYPE,
                    )
                  ),
              )
              .sort(byTextureOrder)
              .map((foodTagOption) => {
                // See the diet-type list: only the tag flagged as this
                // variant's identity is read-only (and warning-coloured).
                const isVariantIdentityTag =
                  isLockedVariantIdentityTag(foodTagOption);
                return (
                  <FoodTagSwitch
                    key={`foodTag${foodTagOption.id}`}
                    foodTag={foodTagOption}
                    onChange={handleTagChange}
                    defaultState={getDefaultState(foodTagOption)}
                    viewOnly={isVariantIdentityTag}
                    warn={isVariantIdentityTag}
                  />
                );
              })}
          </List>
          <List>
            <StyledTagCategoryHeader>
              Suitable Liquid Thickness
            </StyledTagCategoryHeader>
            {foodTagOptions
              .filter(
                (foodTagOption) =>
                  foodTagOption.tag_categories.includes(
                    FoodTagCategory.LIQUID_THICKNESS,
                  ) &&
                  !(
                    foodTagOption.tag_categories.includes(
                      FoodTagCategory.ALLERGEN,
                    ) ||
                    foodTagOption.tag_categories.includes(
                      FoodTagCategory.DIET_TYPE,
                    ) ||
                    foodTagOption.tag_categories.includes(
                      FoodTagCategory.FOOD_TEXTURE,
                    )
                  ),
              )
              .sort(byLiquidThicknessOrder)
              .map((foodTagOption) => {
                // See the diet-type list: only the tag flagged as this
                // variant's identity is read-only (and warning-coloured).
                const isVariantIdentityTag =
                  isLockedVariantIdentityTag(foodTagOption);
                return (
                  <FoodTagSwitch
                    key={`foodTag${foodTagOption.id}`}
                    foodTag={foodTagOption}
                    onChange={handleTagChange}
                    defaultState={getDefaultState(foodTagOption)}
                    viewOnly={isVariantIdentityTag}
                    warn={isVariantIdentityTag}
                  />
                );
              })}
          </List>
          <Divider sx={{ marginTop: "0.75rem" }} />
          <Button
            size="small"
            startIcon={
              isPreferencesShown ? <ExpandLessIcon /> : <ExpandMoreIcon />
            }
            onClick={() => setIsPreferencesShown((previous) => !previous)}
            sx={{ marginTop: "0.5rem" }}
          >
            {isPreferencesShown
              ? "Hide preference tags"
              : "Show preference tags"}
          </Button>
          <Collapse in={isPreferencesShown}>
            {[
              ...PREFERENCE_SUBCATEGORIES.map(
                (subcategory) => subcategory.label,
              ),
              "Other",
            ].map((subcategoryLabel, subcategoryIndex) => {
              const sectionTags = foodTagOptions
                .filter(
                  (foodTagOption) =>
                    foodTagOption.tag_categories.includes(
                      FoodTagCategory.PREFERENCE,
                    ) &&
                    !foodTagOption.tag_categories.includes(
                      FoodTagCategory.VARIANT_TYPE,
                    ) &&
                    preferenceSubcategoryIndex(foodTagOption) ===
                      subcategoryIndex,
                )
                .sort(byTagName);
              if (sectionTags.length === 0) {
                return null;
              }
              return (
                <List key={`preferenceSection${subcategoryLabel}`}>
                  <StyledTagCategoryHeader>
                    Preference - {subcategoryLabel}
                  </StyledTagCategoryHeader>
                  {sectionTags.map((foodTagOption) => {
                    return (
                      <FoodTagSwitch
                        key={`foodTag${foodTagOption.id}`}
                        foodTag={foodTagOption}
                        onChange={handleTagChange}
                        defaultState={getDefaultState(foodTagOption)}
                      />
                    );
                  })}
                </List>
              );
            })}
          </Collapse>
        </>
      ) : null}
    </>
  );
};

// Memoized: the recipe editor re-renders on every step edit and this section
// (dozens of tag switches) is expensive to re-render when its props are unchanged
export default memo(FoodItemTagEditSection);
